<template>
  <oxd-table-filter :filter-title="$t('payroll.employee_configuration')">
    <oxd-form @submit-valid="filterItems">
      <oxd-form-row>
        <oxd-grid :cols="4" class="orangehrm-full-width-grid">
          <oxd-grid-item>
            <employee-autocomplete
              v-model="filters.employee"
              :rules="rules.employee"
              :params="{
                includeEmployees: 'currentAndPast',
              }"
            />
          </oxd-grid-item>
        </oxd-grid>
      </oxd-form-row>

      <oxd-divider />

      <oxd-form-actions>
        <oxd-button
          display-type="ghost"
          :label="$t('general.reset')"
          type="reset"
          @click="onClickReset()"
        />
        <oxd-button
          class="orangehrm-left-space"
          display-type="secondary"
          :label="$t('general.search')"
          type="submit"
        />
      </oxd-form-actions>
    </oxd-form>
  </oxd-table-filter>
  <br />
  <div class="orangehrm-paper-container">
    <div class="orangehrm-header-container">
      <div>
        <oxd-button
          :label="$t('general.add')"
          icon-name="plus"
          display-type="secondary"
          @click="onClickAdd"
        />
      </div>
    </div>
    <table-header
      :total="total"
      :loading="isLoading"
      :selected="checkedItems.length"
      @delete="onClickDeleteSelected"
    ></table-header>
    <div class="orangehrm-container">
      <oxd-card-table
        v-model:selected="checkedItems"
        :headers="headers"
        :items="items?.data"
        :selectable="true"
        :clickable="false"
        :loading="isLoading"
        row-decorator="oxd-table-decorator-card"
      />
    </div>
    <div class="orangehrm-bottom-container">
      <oxd-pagination
        v-if="showPaginator"
        v-model:current="currentPage"
        :length="pages"
      />
    </div>
    <delete-confirmation ref="deleteDialog"></delete-confirmation>
  </div>
</template>

<script>
import {formatEmployeeFullName} from '@/core/util/helper/employeeDisplayName';
import {computed, ref} from 'vue';
import {navigate} from '@/core/util/helper/navigation';
import {APIService} from '@/core/util/services/api.service';
import usePaginate from '@ohrm/core/util/composable/usePaginate';
import {validSelection} from '@/core/util/validation/rules';
import DeleteConfirmationDialog from '@ohrm/components/dialogs/DeleteConfirmationDialog';
import EmployeeAutocomplete from '@/core/components/inputs/EmployeeAutocomplete.vue';
import usei18n from '@/core/util/composable/usei18n';

export default {
  components: {
    'employee-autocomplete': EmployeeAutocomplete,
    'delete-confirmation': DeleteConfirmationDialog,
  },

  setup() {
    const {$t} = usei18n();
    const rules = {
      employee: [validSelection],
    };
    const filters = ref({
      employee: null,
    });

    const serializedFilters = computed(() => {
      return {
        empNumber: filters.value.employee?.id,
      };
    });

    const http = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/payroll/employees/config',
    );
    const deductionRuleNames = {};
    const deductionRuleHttp = new APIService(
      window.appGlobal.baseUrl,
      '/api/v2/payroll/global-deduction-rules',
    );

    const formatBasis = (basis) => {
      return basis === 'daily_attended_annual'
        ? $t('payroll.basic_salary_basis_daily')
        : $t('payroll.basic_salary_basis_monthly');
    };
    const formatExcludedRules = (ids) => {
      if (!Array.isArray(ids) || ids.length === 0) {
        return '-';
      }
      return ids.map((id) => deductionRuleNames[id] || String(id)).join(', ');
    };

    const attendanceRecordNormalizer = (data) => {
      return data.map((item) => {
        const configuration = item.configuration || {};
        return {
          id: item.id,
          employee: formatEmployeeFullName(item.employee),
          deductUnpaidLeave: configuration.deductUnpaidLeave
            ? $t('general.yes')
            : $t('general.no'),
          deductLateIn: configuration.deductLateIn
            ? $t('general.yes')
            : $t('general.no'),
          deductEarlyOut: configuration.deductEarlyOut
            ? $t('general.yes')
            : $t('general.no'),
          basicSalaryBasis: formatBasis(configuration.basicSalaryBasis),
          excludedDeductionRules: formatExcludedRules(
            configuration.excludedGlobalDeductionRuleIds,
          ),
          empNumber: item.empNumber,
        };
      });
    };

    const {
      total,
      pages,
      pageSize,
      response,
      isLoading,
      execQuery,
      currentPage,
      showPaginator,
    } = usePaginate(http, {
      query: serializedFilters,
      normalizer: attendanceRecordNormalizer,
    });
    return {
      http,
      rules,
      total,
      pages,
      filters,
      pageSize,
      isLoading,
      execQuery,
      currentPage,
      showPaginator,
      items: response,
      deductionRuleNames,
      deductionRuleHttp,
    };
  },

  data() {
    return {
      headers: [
        {
          name: 'employee',
          slot: 'title',
          title: this.$t('general.employee_name'),
          style: {flex: 1},
        },
        {
          name: 'deductUnpaidLeave',
          slot: 'title',
          title: this.$t('payroll.deduct_for_unpaid_leave'),
          style: {flex: 1},
        },
        {
          name: 'deductLateIn',
          slot: 'title',
          title: this.$t('payroll.deduct_for_late_in'),
          style: {flex: 1},
        },
        {
          name: 'deductEarlyOut',
          slot: 'title',
          title: this.$t('payroll.deduct_for_early_out'),
          style: {flex: 1},
        },
        {
          name: 'basicSalaryBasis',
          slot: 'title',
          title: this.$t('payroll.basic_salary_basis'),
          style: {flex: 2},
        },
        {
          name: 'excludedDeductionRules',
          slot: 'title',
          title: this.$t('payroll.excluded_deduction_rules'),
          style: {flex: 2},
        },
        {
          name: 'actions',
          slot: 'action',
          title: this.$t('general.actions'),
          style: {flex: 1},
          cellType: 'oxd-table-cell-actions',
          cellRenderer: this.actionRenderer,
        },
      ],
      checkedItems: [],
    };
  },

  created() {
    this.deductionRuleHttp.getAll({limit: 100, offset: 0}).then((response) => {
      const rows = response?.data?.data ?? [];
      rows.forEach((rule) => {
        this.deductionRuleNames[rule.id] = rule.deductionName;
      });
      this.execQuery();
    });
  },
  methods: {
    onClickReset() {
      this.filters = {employee: null};
      this.resetDataTable();
    },
    onClickAdd() {
      navigate('/payroll/addPayrollEmployeeConfig');
    },
    actionRenderer(...[, , , row]) {
      const cellConfig = {};
      cellConfig.edit = {
        onClick: () => this.onClickEdit(row),
        props: {
          name: 'pencil-fill',
        },
      };
      cellConfig.delete = {
        onClick: () => this.onClickDelete(row),
        component: 'oxd-icon-button',
        props: {
          name: 'trash',
        },
      };
      return {
        props: {
          header: {
            cellConfig,
          },
        },
      };
    },
    onClickEdit(item) {
      navigate(`/payroll/editPayrollEmployeeConfig/${item.empNumber}`);
    },
    onClickDeleteSelected() {
      const empNumbers = this.checkedItems.map((index) => {
        return this.items?.data[index].empNumber;
      });
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems(empNumbers);
        }
      });
    },
    onClickDelete(item) {
      this.$refs.deleteDialog.showDialog().then((confirmation) => {
        if (confirmation === 'ok') {
          this.deleteItems([item.empNumber]);
        }
      });
    },
    deleteItems(empNumbers) {
      if (empNumbers instanceof Array) {
        this.isLoading = true;
        Promise.all(
          empNumbers.map((empNumber) =>
            this.http.request({
              url: `/api/v2/payroll/employees/${empNumber}/config`,
              method: 'delete',
            }),
          ),
        )
          .then(() => {
            return this.$toast.deleteSuccess();
          })
          .then(() => {
            this.isLoading = false;
            this.resetDataTable();
          })
          .finally(() => {
            this.isLoading = false;
          });
      }
    },
    async resetDataTable() {
      this.checkedItems = [];
      await this.execQuery();
    },
    async filterItems() {
      await this.execQuery();
    },
  },
};
</script>
